import React, { useMemo, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import countryList from "react-select-country-list";

import { showErrorToast } from "../../apps/AppError";
import { AppSubmitButton, AppTextField } from "../../apps";
import { UPDATE_PERSONAL_DATA } from "../../../graphql/mutations/profile";
import { dispatch } from "../../../store/store";
import { profileActions } from "../../../store/slices/profileSlice";
import { useQueryProfile } from "../../../graphql/apollo/useQuery";
import AppRadio from "../../apps/AppRadio";
import { RadioProps } from "../../../types/components/Form";
import AppSelectField from "../../apps/AppSelectField";
import { Button } from "../../Button";
import { IdentityVerifyIcon } from "../../../assets/svg/IdentifyVerifiyIcon";

const PersonalDataStep = () => {
  const { t } = useTranslation();
  const [gender, setGender] = useState("male");
  const [country, setCountry] = useState("");
  const onInitSteps = (data: any) => {
    const { myProfile } = data;
    dispatch(
      profileActions.initStep({
        isVerifiedEmail: myProfile.isVerifiedEmail,
        isUpdatePersonalData: myProfile.isUpdatePersonalData,
        isVerifiedIdentity: myProfile.isVerifiedIdentity,
      })
    );
  };

  const { refetch, myProfile } = useQueryProfile((data: any) =>
    onInitSteps(data)
  );
  const validationSchema = yup.object({
    email: yup.string(),
    password: yup.string(),
  });

  const [updatePersonalData] = useMutation(UPDATE_PERSONAL_DATA, {
    onCompleted: (data) => {
      if (data.updatePersonalData) {
        refetch();
        dispatch(profileActions.identityStep());
      }
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const handleSubmit = (data: any) => {
    updatePersonalData({
      variables: {
        input: {
          firstname: data.firstname,
          lastname: data.lastname,
          gender,
          birthDate: data.birthDate,
          country,
          city: data.city,
          address: data.address,
          postalCode: data.postalCode,
        },
      },
    });
  };

  const genders: RadioProps[] = [
    { value: "male", label: t("male") },
    { value: "female", label: t("female") },
  ];

  const handleChangeGender = (e: React.ChangeEvent<HTMLInputElement>) =>
    setGender(e.target.value);
  const options = useMemo(() => countryList().getData(), []);

  const handleContinue = () => dispatch(profileActions.identityStep());
  const isVerify = myProfile?.isUpdatePersonalData;

  return (
    <div className="verification-step">
      <Typography variant="h5" sx={{ textAlign: "center" }}>
        {t("personalDetail")}
      </Typography>
      {isVerify ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ textAlign: "center", mb: 3 }} color="green">
            {t("fillDataSuccessfully")}
          </Typography>
          <Box
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <IdentityVerifyIcon />
          </Box>
          <Button title={t("continue")} onClick={handleContinue} />
        </Box>
      ) : (
        <>
          <Typography variant="body1">{t("personalDetailDes")}</Typography>
          <Typography variant="body1" sx={{ mt: 4 }}>
            {t("personalInfo")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t("personalInfoDes1")}
          </Typography>
          <Typography variant="body2">{t("personalInfoDes2")}</Typography>
          <Typography variant="body2">{t("personalInfoDes3")}</Typography>
          <Formik
            validateOnChange
            initialValues={{ email: "", password: "" }}
            onSubmit={(data: any) => handleSubmit(data)}
            validationSchema={validationSchema}
          >
            {() => (
              <Form style={{ textAlign: "left" }} noValidate autoComplete="off">
                <Box>
                  <AppTextField
                    placeholder={t("name")}
                    name="firstname"
                    label={t("name")}
                  />
                  <AppTextField
                    placeholder={t("lastname")}
                    name="lastname"
                    label={t("lastname")}
                  />
                  <Typography variant="caption">{t("nameInfo")}</Typography>
                  <AppRadio
                    value={gender}
                    onChange={(e: any) => handleChangeGender(e)}
                    radios={genders}
                  />
                  <AppTextField
                    placeholder={t("dateOfBirth")}
                    name="birthDate"
                    label={t("dateOfBirth")}
                    type="date"
                  />
                  <AppSelectField
                    options={options}
                    label={t("country")}
                    name="country"
                    onChange={(e) => {
                      setCountry(e);
                    }}
                  />
                  <Divider />
                  <AppTextField
                    placeholder={t("province")}
                    name="city"
                    label={t("province")}
                  />
                  <AppTextField
                    placeholder={t("address")}
                    name="address"
                    label={t("address")}
                  />
                  <AppTextField
                    placeholder={t("postalCode")}
                    name="postalCode"
                    label={t("postalCode")}
                  />
                </Box>
                <AppSubmitButton
                  label={t("submit")}
                  disabled={false}
                  loading={false}
                />
              </Form>
            )}
          </Formik>
        </>
      )}
    </div>
  );
};

export default PersonalDataStep;
